import { CodeBlock } from 'components/ai-elements/code-block';
import { Loader } from 'components/ai-elements/loader';
import { MessageResponse } from 'components/ai-elements/message';
import { cn } from 'lib/utils';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import join from 'lodash/join';
import map from 'lodash/map';
import replace from 'lodash/replace';
import take from 'lodash/take';
import React, { useEffect, useState } from 'react';
import { captureArtifactError } from 'sentry/artifact';
import type { BundledLanguage } from 'shiki';

const MAX_CELLS = 200;
const MAX_OUTPUT_CHARS = 10_000;

type NotebookOutput = {
  output_type: string;
  name?: string;
  text?: string | string[];
  data?: Record<string, unknown>;
  ename?: string;
  evalue?: string;
  traceback?: string[];
};

type NotebookCell = {
  cell_type: 'markdown' | 'code' | 'raw';
  source: string | string[];
  execution_count?: number | null;
  outputs?: NotebookOutput[];
};

type Notebook = {
  nbformat: number;
  cells: NotebookCell[];
  metadata?: {
    language_info?: { name?: string };
    kernelspec?: { language?: string };
  };
};

/** nbformat stores text as either a string or a list of lines. */
const joinSource = (source: string | string[] | undefined): string =>
  isArray(source) ? join(source, '') : source || '';

/** Strip ANSI color escape codes that Jupyter embeds in tracebacks. */
// eslint-disable-next-line no-control-regex
const ANSI_RE = /\u001b\[[0-9;]*m/g;
const stripAnsi = (text: string): string => replace(text, ANSI_RE, '');

const truncateText = (text: string): { text: string; truncated: boolean } =>
  text.length > MAX_OUTPUT_CHARS
    ? { text: text.slice(0, MAX_OUTPUT_CHARS), truncated: true }
    : { text, truncated: false };

const OutputText = ({ text, className }: { text: string; className?: string }) => {
  const { text: shown, truncated } = truncateText(text);
  return (
    <div>
      <pre
        className={cn(
          'overflow-x-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 font-mono text-xs',
          className,
        )}
      >
        {shown}
      </pre>
      {truncated && (
        <p className="mt-1 text-xs text-muted-foreground">Output truncated</p>
      )}
    </div>
  );
};

const CellOutput = ({ output }: { output: NotebookOutput }) => {
  if (output.output_type === 'stream') {
    return (
      <OutputText
        text={joinSource(output.text)}
        className={output.name === 'stderr' ? 'text-amber-600 dark:text-amber-400' : undefined}
      />
    );
  }
  if (output.output_type === 'error') {
    const traceback = isArray(output.traceback)
      ? join(map(output.traceback, stripAnsi), '\n')
      : `${output.ename || 'Error'}: ${output.evalue || ''}`;
    return <OutputText text={traceback} className="bg-destructive/10 text-destructive" />;
  }
  if (output.output_type === 'execute_result' || output.output_type === 'display_data') {
    const data = output.data || {};
    // MIME preference order: images first, then plain text. text/html is
    // deliberately skipped — notebook HTML is arbitrary and rendering it
    // same-origin would be an injection surface. If HTML output support is
    // ever needed, render it in a sandboxed iframe like JSXDashboard does.
    for (const mime of ['image/png', 'image/jpeg']) {
      const b64 = data[mime];
      if (isString(b64)) {
        return (
          <img
            src={`data:${mime};base64,${replace(b64, /\n/g, '')}`}
            alt="Notebook output"
            className="max-w-full rounded-md border border-border bg-white"
          />
        );
      }
    }
    const plain = data['text/plain'];
    if (plain !== undefined) {
      return <OutputText text={joinSource(plain as string | string[])} />;
    }
  }
  return null;
};

const NotebookCellView = ({ cell, language }: { cell: NotebookCell; language: BundledLanguage }) => {
  const source = joinSource(cell.source);

  if (cell.cell_type === 'markdown') {
    return <MessageResponse>{source}</MessageResponse>;
  }

  if (cell.cell_type === 'code') {
    return (
      <div className="space-y-2">
        {!isNil(cell.execution_count) && (
          <p className="font-mono text-xs text-muted-foreground">
            In [{cell.execution_count}]
          </p>
        )}
        <CodeBlock code={source} language={language} />
        {map(cell.outputs, (output, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <CellOutput key={i} output={output} />
        ))}
      </div>
    );
  }

  // raw cells
  return (
    <pre className="overflow-x-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 font-mono text-xs">
      {source}
    </pre>
  );
};

type NotebookViewerProps = {
  url: string;
};

/**
 * Read-only renderer for Jupyter notebooks (.ipynb, nbformat 4+) produced by
 * the chatbot or uploaded by users. Reuses the chat's markdown and code-block
 * components so notebooks match the app theme without a notebook library.
 */
export const NotebookViewer = ({ url }: NotebookViewerProps) => {
  const [notebook, setNotebook] = useState<Notebook | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load notebook: ${res.status}`);
        return res.json();
      })
      .then((nb: Notebook) => {
        if (cancelled) return;
        if (!nb || !isArray(nb.cells) || !nb.nbformat || nb.nbformat < 4) {
          throw new Error('Unsupported notebook format (nbformat 4+ required)');
        }
        setNotebook(nb);
      })
      .catch((err) => {
        if (cancelled) return;
        captureArtifactError(err, { url, kind: 'notebook' });
        setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (notebook === null) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader />
      </div>
    );
  }

  const language = (notebook.metadata?.language_info?.name
    || notebook.metadata?.kernelspec?.language
    || 'python') as BundledLanguage;
  const cells = take(notebook.cells, MAX_CELLS);

  return (
    <div className="space-y-4">
      {map(cells, (cell, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <NotebookCellView key={i} cell={cell} language={language} />
      ))}
      {notebook.cells.length > MAX_CELLS && (
        <p className="text-xs text-muted-foreground">
          Showing first {MAX_CELLS} of {notebook.cells.length} cells — download the notebook for
          the full content.
        </p>
      )}
    </div>
  );
};
